import { ApplicationConfig, inject, provideAppInitializer, SecurityContext } from '@angular/core';
import { provideRouter, Routes } from '@angular/router';
import { LoginComponent } from './pages/login/login.component';
import { userRoute } from './pages/user/user.config';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { HomeComponent } from './pages/home/home.component';
import { canLogin, canActivateCourses, canActivateProfile, canActivateTracks, isAuthenticated, notAuthenticated } from './shared/guard/guard';
import { PageKey } from './shared/services/page/models/page.key';
import { ProfileComponent } from './pages/profile/profile.component';
import { ForgotPwdComponent } from './pages/forgot-pwd/forgot-pwd.component';
import { ResetPwdComponent } from './pages/reset-pwd/reset-pwd.component';
import { FirstAccessComponent } from './pages/first-access/first-access.component';
import { UserCoursesComponent } from './pages/courses/courses.component';
import { CourseDetailsComponent } from './pages/courses/details/details.component';
import { ConfirmationService, MessageService } from 'primeng/api';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, provideHttpClient, withInterceptors } from '@angular/common/http';
import { environment } from '../environments/environment';
import { catchError, EMPTY, from, Observable, switchMap, throwError } from 'rxjs';
import { AuthService } from './shared/services/auth/auth.service';
import { jwtDecode } from 'jwt-decode'
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { providePrimeNG } from 'primeng/config';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { TenantThemeService } from './shared/services/tenant-theme/tenant-theme.service';
import { CourseContentComponent } from './pages/courses/content/content.component';
import { MARKED_OPTIONS, provideMarkdown } from 'ngx-markdown';
import { TracksComponent } from './pages/tracks/tracks.component';
import { TrackDetailsComponent } from './pages/tracks/details/details.component';
import { ImpersonationEntryComponent } from './pages/impersonation-entry/impersonation-entry.component';
import { ensureBearerToken, stripBearerToken } from './shared/utils/bearer-token.util';

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [canLogin],
    data: {
      title: 'Login',
      fullscreen: true,
      key: PageKey.LOGIN,
    },
  },
  {
    path: 'profile',
    component: ProfileComponent,
    canActivate: [isAuthenticated, canActivateProfile],
    data: {
      title: 'Perfil',
    },
  },
  {
    path: 'forgot/password',
    component: ForgotPwdComponent,
    canActivate: [notAuthenticated],
    data: {
      title: 'Esqueci Senha',
      fullscreen: true,
    },
  },
  {
    path: 'reset/password',
    component: ResetPwdComponent,
    canActivate: [notAuthenticated],
    data: {
      title: 'Nova Senha',
      fullscreen: true,
    },
  },
  {
    path: 'first-access',
    component: FirstAccessComponent,
    canActivate: [notAuthenticated],
    data: {
      title: 'Primeiro Acesso',
      fullscreen: true,
      hideHeader: true,
    },
  },
  {
    path: 'home',
    component: HomeComponent,
    canActivate: [isAuthenticated],
    data: {
      title: 'Home',
      key: PageKey.HOME,
      search: false,
    },
  },
  {
    path: 'courses',
    component: UserCoursesComponent,
    canActivate: [isAuthenticated, canActivateCourses],
    data: {
      title: 'Meus Cursos',
      key: PageKey.COURSES,
    },
  },
  {
    path: 'courses/:courseId',
    component: CourseDetailsComponent,
    canActivate: [isAuthenticated, canActivateCourses],
    data: {
      title: 'Detalhes do Curso',
      key: PageKey.COURSES,
      search: false,
      fullscreen: true,
    },
  },
  {
    path: 'courses/:courseId/content',
    component: CourseContentComponent,
    canActivate: [isAuthenticated, canActivateCourses],
    data: {
      title: 'Conteúdo do Curso',
      key: PageKey.COURSES,
      search: false,
      fullscreen: true,
    },
  },
  {
    path: 'tracks',
    component: TracksComponent,
    canActivate: [isAuthenticated, canActivateTracks],
    data: {
      title: 'Minhas Trilhas',
      key: PageKey.TRACKS,
    },
  },
  {
    path: 'tracks/:trackId',
    component: TrackDetailsComponent,
    canActivate: [isAuthenticated, canActivateTracks],
    data: {
      title: 'Detalhes da Trilha',
      key: PageKey.TRACKS,
    },
  },
  {
    path: 'impersonate',
    component: ImpersonationEntryComponent,
    data: {
      title: 'Impersonação',
      fullscreen: true,
      hideHeader: true,
      impersonationEnvironment: 'student',
    },
  },
  ...userRoute,
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: '**', component: NotFoundComponent, data: { title: 'Página não encontrada', fullscreen: true, hideHeader: true } },
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    providePrimeNG(),
    provideMarkdown({
      sanitize: SecurityContext.NONE,
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {
          gfm: true,
          breaks: true,
          pedantic: false
        },
      },
    }),
    provideHttpClient(
      withInterceptors([baseUrlInterceptor, refreshTokenInterceptor, unauthorizedInterceptor, authInterceptor]),
    ),
    provideEnvironmentNgxMask(),
    provideAppInitializer(() => {
      const tenantThemeService = inject(TenantThemeService);
      tenantThemeService.applyStoredTheme();
    }),
    BrowserAnimationsModule,
    MessageService,
    ConfirmationService,
  ],

};

export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const token = authService.getToken()
  const tenantSlug = 'unico-master';

  const headers = req.headers.set('x-tenant-slug', tenantSlug);

  if (token) {
    const newReq = req.clone({
      headers: headers.append('Authorization', ensureBearerToken(token.token)),
    });

    return next(newReq);
  }

  return next(req.clone({ headers }));
}

export function refreshTokenInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const token = authService.getToken()

  if (!token)
    return next(req);

  const fiveSecondsInMilliseconds = 1_000 * 5;
  const maxSafeExpiresDate = +new Date() + fiveSecondsInMilliseconds;

  if (!isTokenExpired(token.token, maxSafeExpiresDate))
    return next(req);

  // Refresh token vencido: não há como renovar, então encerra a sessão e cancela a requisição
  if (!token.refreshToken || isTokenExpired(token.refreshToken, maxSafeExpiresDate)) {
    void authService.expireSession();
    return EMPTY;
  }

  // Espera o novo token antes de seguir, para a requisição não sair com o token vencido
  return from(authService.refreshToken()).pipe(
    switchMap((refreshed) => {
      if (!refreshed) {
        void authService.expireSession();
        return EMPTY;
      }

      return next(req);
    }),
  );
}

export function unauthorizedInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: unknown) => {
      // 401 com sessão salva significa token rejeitado pela API (expirado ou revogado)
      if (error instanceof HttpErrorResponse && error.status === 401 && authService.getToken())
        void authService.expireSession();

      return throwError(() => error);
    }),
  );
}

export function baseUrlInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const learningRoutes = ['/courses', '/categories', '/tracks', '/modules', '/contents', '/certificates'];
  const isLearningRoute = learningRoutes.some((route) => req.url.startsWith(route));
  const baseUrl = isLearningRoute ? environment.learningApiUrl : environment.apiUrl;

  const newReq = req.clone({
    url: `${ baseUrl }${ req.url }`,
  });

  return next(newReq);
}

export function isTokenExpired(token: string, maxExpiresDate: number): boolean {
  const jwtPayload: { exp: number } = jwtDecode(stripBearerToken(token));
  return maxExpiresDate >= +new Date(jwtPayload.exp * 1000);
}
